const SCROLL_THRESHOLD = 2;
const attached = new WeakSet();
// Scroll container → { observer, headers } for the header cells its sticky offsets are measured from
const headerWatches = new WeakMap();

function syncScrollClass(el) {
  const scrolled = el.scrollLeft > SCROLL_THRESHOLD;
  el.classList.toggle("is-scrolled", scrolled);
}

/**
 * Column widths can change while the table keeps its size (new data, longer names in another column),
 * which the container/table observers miss: watch the measured header cells too.
 * Only newly rendered cells are observed — observing a cell again would re-fire the observer every frame.
 */
function watchStickyHeaders(el, headers) {
  const watch = headerWatches.get(el);
  if (!watch) return;
  watch.headers.forEach((th) => {
    if (!headers.includes(th)) watch.observer.unobserve(th);
  });
  headers.forEach((th) => {
    if (!watch.headers.includes(th)) watch.observer.observe(th);
  });
  watch.headers = headers;
}

/** Match col-2/col-3 `left` to measured widths so sticky columns never overlap. */
function syncStickyColumnOffset(el) {
  const table = el.querySelector("table:not([data-export-only])");
  if (!table) return;

  const headers = table.querySelectorAll("thead th");
  const firstHeader = headers[0];
  if (!firstHeader) return;
  watchStickyHeaders(el, [firstHeader, headers[1]].filter(Boolean));

  const col1Width = Math.ceil(firstHeader.getBoundingClientRect().width);
  if (col1Width > 0) {
    el.style.setProperty("--coa-sticky-col2-left", `${col1Width}px`);
  }

  const secondHeader = headers[1];
  if (secondHeader) {
    const col2Width = Math.ceil(secondHeader.getBoundingClientRect().width);
    if (col1Width > 0 && col2Width > 0) {
      el.style.setProperty("--coa-sticky-col3-left", `${col1Width + col2Width}px`);
    }
  }
}

function syncCoaTableScroll(el) {
  syncStickyColumnOffset(el);
  if (el.scrollWidth <= el.clientWidth + SCROLL_THRESHOLD) {
    el.classList.remove("is-scrolled");
  } else {
    syncScrollClass(el);
  }
}

function attachCoaTableScrollShadow(el) {
  if (!el?.classList?.contains("coa-table-scroll") || attached.has(el)) {
    return;
  }
  attached.add(el);

  const onScroll = () => syncCoaTableScroll(el);
  el.addEventListener("scroll", onScroll, { passive: true });

  const resizeObserver =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => syncCoaTableScroll(el))
      : null;

  if (resizeObserver) {
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }
    headerWatches.set(el, { observer: resizeObserver, headers: [] });
  }

  requestAnimationFrame(() => syncCoaTableScroll(el));
}

export function initCoaTableScrollShadow(root = document) {
  const scope = root?.body ?? root;
  if (!scope?.querySelectorAll) {
    return () => {};
  }

  scope.querySelectorAll(".coa-table-scroll").forEach(attachCoaTableScrollShadow);

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.classList?.contains("coa-table-scroll")) {
          attachCoaTableScrollShadow(node);
        }
        node.querySelectorAll?.(".coa-table-scroll").forEach(attachCoaTableScrollShadow);
      });
    }
  });

  mutationObserver.observe(scope, { childList: true, subtree: true });

  return () => mutationObserver.disconnect();
}
